import React, {
	memo,
	forwardRef,
	useImperativeHandle,
	useState,
	useCallback,
	useRef,
	useEffect,
} from "react";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import Grid from "@mui/material/Grid";
import { Box, Typography } from "@mui/material";

import AppSelectInput from "components/app-select-input";
import AppCalendarInput from "components/app-calendar-input";
import AppTimeInput from "components/app-time-input";
import AppButton from "components/app-button";

import getAllEmployeeListing from "services/get-all-employee-listing";
import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import sanitizeObject from "common/sanitize-object";
import ERRORS from "common/errors";
import * as calendar from "common/calendar";
import store from "store/store";
import { promptAlertMessage } from "store/slices/alert";
import AppInput from "components/app-input";

const CreateAppointmentModal = (props, ref) => {
	const [visible, setVisible] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [mentorTime, setmentorTime] = useState([]);
	const [menteeTime, setmenteeTime] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [blockedTimes, setBlockedTimes] = useState([]);
	const [startTimeRangeCheck, setStartTimeRangeCheck] = useState([]);
	const [endTimeRangeCheck, setEndTimeRangeCheck] = useState([]);
	const employeeRef = useRef();
	const mentorRef = useRef();
	const dispatch = store.dispatch;

	const calendarDisplayFormat =
		calendar.CALENDAR_FORMAT.DATE_FORMAT +
		" " +
		calendar.CALENDAR_FORMAT.MONTH_FORMAT +
		" " +
		calendar.CALENDAR_FORMAT.YEAR_FORMAT;

	const fetchUserAvailability = async (event, target) => {
		// Reset time values
		// formik.setFieldValue("appointmentStartTime", "");
		// formik.setFieldValue("appointmentEndTime", "");

		let payload = {
			employeeId: null,
		};

		if (target === "EMPLOYEE") {
			const empOpts = employeeRef.current.getOptionsData();
			const selectedEmp = empOpts.find((o) => o.value === event);
			payload.employeeId = selectedEmp?.id ?? null;
		}

		if (target === "MENTOR") {
			const mentOpts = mentorRef.current.getOptionsData();
			const selectedMentor = mentOpts.find((o) => o.value === event);
			payload.employeeId = selectedMentor?.id ?? null;
		}

		try {
			const response = await api.get.appointment.checkAvailability(payload);

			if (response.length > 0) {
				response.forEach((element) => {
					element.start = element.appointmentStartTime.slice(0, 5);
					element.end = element.appointmentEndTime.slice(0, 5);
					element.formatedDate = element.appointmentDate.slice(0, 10);
				});

				if (target === "EMPLOYEE") {
					setmenteeTime([]);
					setmenteeTime(response);
				}

				if (target === "MENTOR") {
					setmentorTime([]);
					setmentorTime(response);
				}
			}
		} catch (error) {
			// Optionally handle error
		}
	};

	const onHandleConfirm = useCallback(
		async (values, formikHelpers) => {
			setIsSubmitting(true);
			try {
				const empOpts = employeeRef.current.getOptionsData();
				const mentOpts = mentorRef.current.getOptionsData();
				const selectedEmp = empOpts.find((o) => o.value === values.employeeDetailId);
				const selectedMentor = mentOpts.find(
					(o) => o.value === values.mentorEmployeeDetailId
				);

				const payload = {
					employeeDetailId: selectedEmp?.id,
					mentorEmployeeDetailId: selectedMentor?.id,
					remark: values.remark,
					appointmentDate: values.appointmentDate,
					appointmentStartTime: values.appointmentStartTime,
					appointmentEndTime: values.appointmentEndTime,
				};

				await api.post.appointment.create(sanitizeObject(payload));
				props.onHandleGetList();
				formikHelpers.resetForm();
				//resetAll();
				setVisible(false);
			} catch (error) {
				serveRequestErrors(error);
			} finally {
				setIsSubmitting(false);
			}
		},
		[props]
	);	

	const formik = useFormik({
		initialValues: {
			employeeDetailId: "",
			mentorEmployeeDetailId: "",
			remark: "",
			appointmentDate: null,
			appointmentStartTime: "10:00",
			appointmentEndTime: "11:00",
		},
		validationSchema: yup.object({
			employeeDetailId: yup
				.string()
				.required("Employee is required")
				.test("not-same-as-mentor", "Employee and Mentor must differ", function (value) {
					const mentorEmployeeDetailId = this.parent.mentorEmployeeDetailId;
					if (mentorEmployeeDetailId && value === mentorEmployeeDetailId) {
						return false;
					}
					return true;
				}),

			mentorEmployeeDetailId: yup
				.string()
				.required("Mentor is required")
				.test("not-same-as-employee", "Mentor and Employee must differ", function (value) {
					const employeeDetailId = this.parent.employeeDetailId;
					if (employeeDetailId && value === employeeDetailId) {
						return false;
					}
					return true;
				}),
			appointmentDate: yup.date().required(ERRORS.REQUIRED),
			appointmentStartTime: yup
				.string()
				.required(ERRORS.REQUIRED)
				.test("time-invalid", "Cannot choose time pass / time ongoing", function (val) {
					const today = new Date();
					const timeNow = today.toTimeString().slice(0, 5);
					const pickedDate = new Date(this.parent.appointmentDate);
					if (pickedDate.toDateString() === today.toDateString()) {
						return calendar.formatToMinutes(timeNow) < calendar.formatToMinutes(this.parent.appointmentStartTime);
					}
					return true;
				}),
			appointmentEndTime: yup
				.string()
				.required(ERRORS.REQUIRED)
				.test("time-invalid", "Cannot choose time pass / time ongoing", function (val) {
					const today = new Date();
					const timeNow = today.toTimeString().slice(0, 8);
					const pickedDate = new Date(this.parent.appointmentDate);
					if (pickedDate.toDateString() === today.toDateString()) {
						return calendar.formatToMinutes(timeNow) < calendar.formatToMinutes(this.parent.appointmentEndTime);
					}
					return true;
				})
				.test("is-after-start", "End time must be after start time", function (val) {
					return val > this.parent.appointmentStartTime;
				}),
		}),
		onSubmit: onHandleConfirm,
		validateOnChange: true,
	});

	const resetAll = useCallback (() => {
		formik.resetForm();
		setSelectedDate(null);
		setmentorTime([]);
		setmenteeTime([]);
		setBlockedTimes([]);
		setIsSubmitting(false);
	}, [formik]);

	const changeAvailability = useCallback (async (event) => {
		setBlockedTimes([]);

		if (event != null) {
			const targetDate = event.split ? event.split("T")[0] : calendar.formatDatePattern(event, "YYYY-MM-DD");

			const filteredMentor = mentorTime.filter((item) => item.formatedDate === targetDate);
			const filteredMentee = menteeTime.filter((item) => item.formatedDate === targetDate);

			const pickFields = ({ start, end }) => ({ start, end });
			const mentorSlim = filteredMentor.map(pickFields);
			const menteeSlim = filteredMentee.map(pickFields);

			const newAvailabilityTime = [...mentorSlim, ...menteeSlim];
			setBlockedTimes(newAvailabilityTime);
		}
	}, [mentorTime, menteeTime]);

	useEffect(() => {
		changeAvailability(selectedDate);
	}, [mentorTime, menteeTime, changeAvailability, selectedDate]);

	const handleChangeDate = async (value) => {
		changeAvailability(value);
		setSelectedDate(value);
		// formik.setFieldValue("appointmentStartTime", "");
		// formik.setFieldValue("appointmentEndTime", "");
	};	

	const handleClose = () => {
		resetAll();
		setVisible(false);
	};

	useImperativeHandle(ref, () => ({
		onHandleShow: () => setVisible(true),
		onHandleDismiss: () => {
			resetAll();
			setVisible(false);
		},
	}));

	if (!visible) return null;

	const handleStartTimeChange = (name, value) => {
		setStartTimeRangeCheck(value);

		if (TimeCheck(value)) {
			dispatch(promptAlertMessage({
				error: true, message: "This time slot is already occupied"
			}));

			// formik.setFieldValue("appointmentStartTime", "");
		}
		formik.setFieldValue(name, value);
	};

	const handleEndTimeChange = (name, value) => {
		setEndTimeRangeCheck(value);

		if (TimeCheck(value)) {
			dispatch(promptAlertMessage({
				error: true, message: "This time slot is already occupied"
			}));

			// formik.setFieldValue("appointmentEndTime", "");
		}
		formik.setFieldValue(name, value);
	};

	const TimeCheck = (time) => {
		return (
			blockedTimes.some(({ start, end }) => time >= start && time <= end) ||
			blockedTimes.some(({ start, end }) => end > startTimeRangeCheck && endTimeRangeCheck > start)
		);
	};


	const handleEmployeeChange = async (e) => {
		await formik.setFieldValue("employeeDetailId", e.target.value);
		formik.setFieldTouched("employeeDetailId", true, true);
		formik.setFieldTouched("mentorEmployeeDetailId", true, true);
		fetchUserAvailability(e.target.value, "EMPLOYEE");
	};

	const handleMentorChange = async (e) => {
		await formik.setFieldValue("mentorEmployeeDetailId", e.target.value);
		formik.setFieldTouched("mentorEmployeeDetailId", true, true);
		formik.setFieldTouched("employeeDetailId", true, true);
		fetchUserAvailability(e.target.value, "MENTOR");
	};


	return (
		<Modal
			className="app-create-position-modal"
			open={visible}
			onClose={handleClose}
			aria-labelledby="position-modal"
		>
			<Box
				p={4}
				bgcolor="background.paper"
				borderRadius={2}
				className="modal-box"
			>
				<Typography variant="h6" gutterBottom>
					Schedule Appointment
				</Typography>

				<form onSubmit={formik.handleSubmit}>
					<Grid container spacing={2}>
						{/* Employee */}
						<Grid item xs={6}>
							<AppSelectInput
								ref={employeeRef}
								name="employeeDetailId"
								label="Employee"
								validateOnChange={true}
								placeholder="Select Employee"
								loadOptions={getAllEmployeeListing}
								value={formik.values.employeeDetailId}
								onChange={handleEmployeeChange}
								error={formik.errors.employeeDetailId}
								touched={formik.touched.employeeDetailId}
							/>
						</Grid>

						{/* Date */}
						<Grid item xs={6}>
							<AppCalendarInput
								name="appointmentDate"
								label="Date"
								placeholder="Select Date"
								minDate={new Date()}
								value={formik.values.appointmentDate}
								onChange={(name, value) => {
									formik.setFieldValue(name, value);
									handleChangeDate(value);
								}}
								error={formik.errors.appointmentDate}
								touched={formik.touched.appointmentDate}
								displayFormat={calendarDisplayFormat}
							/>
						</Grid>

						{/* Mentor */}
						<Grid item xs={6}>
							<AppSelectInput
								ref={mentorRef}
								name="mentorEmployeeDetailId"
								label="Mentor"
								validateOnChange={true}
								placeholder="Select Mentor"
								loadOptions={getAllEmployeeListing}
								value={formik.values.mentorEmployeeDetailId}
								onChange={handleMentorChange}
								error={formik.errors.mentorEmployeeDetailId}
								touched={formik.touched.mentorEmployeeDetailId}
							/>
						</Grid>

						{/* Start Time */}
						<Grid item xs={6}>
							<AppTimeInput
								required
								name="appointmentStartTime"
								label="Start Time"
								placeholder="--:--"
								value={formik.values.appointmentStartTime}
								error={formik.errors.appointmentStartTime}
								touched={formik.touched.appointmentStartTime}
								disabled={isSubmitting}
								onChange={handleStartTimeChange}
							/>
						</Grid>

						{/* Remark */}
						<Grid item xs={6}>
							<AppInput
								fullWidth
								multiline
								rows={4}
								name="remark"
								label="Remark (Optional)"
								maxLength={255}
								value={formik.values.remark}
								onChange={(e) => {
									formik.handleChange(e);
									formik.setFieldTouched("remark", true, false);
								}}
								error={Boolean(formik.touched.remark && formik.errors.remark)}
								helperText={formik.touched.remark && formik.errors.remark}
							/>
						</Grid>

						{/* End Time */}
						<Grid item xs={6}>
							<AppTimeInput
								required
								name="appointmentEndTime"
								label="End Time"
								placeholder="--:--"
								minTime={formik.values.appointmentStartTime}
								value={formik.values.appointmentEndTime}
								error={formik.errors.appointmentEndTime}
								touched={formik.touched.appointmentEndTime}
								disabled={isSubmitting}
								onChange={handleEndTimeChange}
							/>
						</Grid>
					</Grid>

					<Grid container spacing={2} sx={{ mt: 2 }}>
						<Grid item xs={6}>
							<AppButton
								type="button"
								label="Cancel"
								outline
								onClick={handleClose}
							/>
						</Grid>
						<Grid item xs={6}>
							<AppButton type="submit" label="Create" disabled={isSubmitting} />
						</Grid>
					</Grid>
				</form>
			</Box>
		</Modal>
	);
};

export default memo(forwardRef(CreateAppointmentModal));

CreateAppointmentModal.propTypes = {
	onHandleGetList: PropTypes.func.isRequired,
};
